import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import throttle from "lodash.throttle";

import { useIoCContext } from "@context/IoCContext/IoCContext";
import { Types } from "@ioc/types";
import { IAuthService } from "@modules/user/models/IAuthService";
import { IBlockUserService } from "@modules/user/models/IBlockUserService";
import { IListUsersService } from "@modules/user/models/IListUsersService";
import { IUnlockUserService } from "@modules/user/models/IUnlockUserService";
import { IPagination } from "@modules/infra/http/models/IPaginatedResponse";
import AppError from "@utils/AppError";
import { IUser } from "@utils/interfaces";
import { calcPagesQuantity } from "@utils/pagination";
import { useFlags } from "flagsmith/react";

const ROWS_PER_PAGE = 10;

const EMPTY_PAGINATION: IPagination = {
  limit: ROWS_PER_PAGE,
  offset: 0,
  total: 0,
  hasMore: false,
};

export function useListUsers() {
  const { enqueueSnackbar } = useSnackbar();
  const iocContext = useIoCContext();

  const listUsersService = iocContext.serviceContainer.get<IListUsersService>(
    Types.Users.IListUsersService
  );
  const blockUserService = iocContext.serviceContainer.get<IBlockUserService>(
    Types.Users.IBlockUserService
  );
  const unlockUserService = iocContext.serviceContainer.get<IUnlockUserService>(
    Types.Users.IUnlockUserService
  );
  const authService = iocContext.serviceContainer.get<IAuthService>(
    Types.Users.IAuthService
  );

  const flag = useFlags(["is_carrier_type_user"]);
  const isCarrierEnabled = flag.is_carrier_type_user.enabled;

  const [users, setUsers] = useState<IUser[]>([]);
  const [pagination, setPagination] = useState<IPagination>(EMPTY_PAGINATION);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchUsers, setSearchUsers] = useState("");
  const [page, setPage] = useState(0);
  const [active, setActive] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const fetchUsers = useCallback(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingUsers(true);
        const { data, pagination: pageInfo } = await listUsersService.execute({
          search: searchUsers || undefined,
          enabled: active,
          limit: ROWS_PER_PAGE,
          offset: page * ROWS_PER_PAGE,
        });
        if (mounted) {
          setUsers(data);
          setPagination(pageInfo);
        }
      } catch {
        if (mounted) {
          setUsers([]);
          setPagination(EMPTY_PAGINATION);
          enqueueSnackbar("Ocorreu um erro ao recuperar usuários", {
            variant: "error",
          });
        }
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar, listUsersService, searchUsers, active, page]);

  useEffect(() => {
    const cleanup = fetchUsers();
    return cleanup;
  }, [fetchUsers]);

  // O filtro de `carrier` (feature flag `is_carrier_type_user`) permanece
  // client-side e atua sobre a página já paginada pelo backend. Limitação
  // conhecida: quando o flag está off, a página exibida pode conter menos que
  // ROWS_PER_PAGE itens, e a contagem visual pode divergir de `pagination.total`
  // (que inclui carriers). Levar esse filtro ao backend é escopo de outra change.
  const paginatedUsers = useMemo(() => {
    if (!isCarrierEnabled) return users.filter((u) => u.kind !== "carrier");
    return users;
  }, [users, isCarrierEnabled]);

  const totalItems = pagination.total;
  const totalPages = calcPagesQuantity(ROWS_PER_PAGE, totalItems);

  const handleBlockUser = useCallback(
    async (userID: string) => {
      try {
        const result = await blockUserService.execute(userID);
        enqueueSnackbar(result.message, { variant: "success" });
      } catch (error) {
        const msg =
          error instanceof AppError
            ? error.message
            : "Ocorreu um erro ao bloquear usuário";
        enqueueSnackbar(msg, {
          variant: error instanceof AppError ? error.variant : "error",
        });
      } finally {
        setShowBlockModal(false);
        fetchUsers();
      }
    },
    [blockUserService, enqueueSnackbar, fetchUsers]
  );

  const handleUnlockUser = useCallback(
    async (userID: string) => {
      try {
        const result = await unlockUserService.execute(userID);
        enqueueSnackbar(result.message, { variant: "success" });
      } catch (error) {
        const msg =
          error instanceof AppError
            ? error.message
            : "Ocorreu um erro ao desbloquear usuário";
        enqueueSnackbar(msg, {
          variant: error instanceof AppError ? error.variant : "error",
        });
      } finally {
        setShowBlockModal(false);
        fetchUsers();
      }
    },
    [unlockUserService, enqueueSnackbar, fetchUsers]
  );

  const handleResetPassword = async (email: string) => {
    if (!email) return;
    try {
      setResetLoading(true);
      await authService.forgotPassword(email);
      enqueueSnackbar(
        `Instruções de redefinição de senha enviadas para ${email}`,
        { variant: "success", autoHideDuration: 6000 }
      );
    } catch (e) {
      enqueueSnackbar(
        e instanceof AppError
          ? e.message
          : "Ocorreu um erro ao tentar redefinir a senha.",
        { variant: "error" }
      );
    } finally {
      setResetLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledSearch = useCallback(
    throttle((v: string) => {
      // Nova busca reinicia a paginação para a primeira página.
      setPage(0);
      setSearchUsers(v);
    }, 1000),
    []
  );

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
    setActive(newValue === 0);
    setPage(0);
  };

  return {
    loadingUsers,
    paginatedUsers,
    totalItems,
    totalPages,
    page,
    setPage,
    active,
    tabIndex,
    handleTabChange,
    showBlockModal,
    setShowBlockModal,
    selectedUserId,
    setSelectedUserId,
    showDetailsModal,
    setShowDetailsModal,
    selectedUser,
    setSelectedUser,
    handleBlockUser,
    handleUnlockUser,
    handleResetPassword,
    resetLoading,
    throttledSearch,
    fetchUsers,
  };
}
